import React, { useState } from 'react';
import { Card, Table, Button, Space, Tooltip, Spin, Empty, Typography, Tag } from 'antd';
import { ReloadOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useRecentDeposits } from '../../hooks/useIndexerManagement';
import { DepositTransaction } from '../../types/indexer';
import { useNavigate } from 'react-router-dom';
import FixedStatusTag from './FixedStatusTag';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

const { Text } = Typography;

const RecentDepositsPanel: React.FC = () => {
  const { t: indexerT } = useTranslationWithNamespace('indexer');
  const { t: commonT } = useTranslationWithNamespace('common');
  const navigate = useNavigate();

  // State for pagination and filtering
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Calculate offset based on pagination
  const offset = (pagination.current - 1) * pagination.pageSize;

  // Fetch data with pagination
  const { data: deposits, isLoading, isError, refetch } = useRecentDeposits(
    pagination.pageSize,
    offset
  );

  // Handle table change (pagination, filters, sorter)
  const handleTableChange = (newPagination: any, filters: any) => {
    setPagination(newPagination);
    setStatusFilter(filters.status && filters.status.length > 0 ? filters.status[0] : null);
  };

  // Format amount with appropriate precision
  const formatAmount = (amount: string | undefined) => {
    if (!amount) return '0.00';

    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00';

    return num < 0.01 ? num.toFixed(8) : num.toFixed(4);
  };

  // Status filter options
  const statusFilters = [
    { text: 'Detected', value: 'Detected' },
    { text: 'Received', value: 'Received' },
    { text: 'TopUpNeeded', value: 'TopUpNeeded' },
    { text: 'TopUpPending', value: 'TopUpPending' },
    { text: 'Confirmed', value: 'Confirmed' },
    { text: 'Processing', value: 'Processing' },
    { text: 'Completed', value: 'Completed' },
    { text: 'Failed', value: 'Failed' }
  ];

  // Table columns
  const columns = [
    {
      title: indexerT('deposits.txid'),
      dataIndex: 'txid',
      key: 'txid',
      render: (txid: string | undefined) => {
        if (!txid) {
          return <Text type="secondary">{indexerT('deposits.noTxid', 'No TXID')}</Text>;
        }
        return (
          <Tooltip title={txid}>
            <Text copyable={{ text: txid }}>
              {`${txid.substring(0, 8)}...${txid.substring(txid.length - 8)}`}
            </Text>
          </Tooltip>
        );
      }
    },
    {
      title: indexerT('deposits.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: string | undefined) => formatAmount(amount),
      sorter: (a: DepositTransaction, b: DepositTransaction) => {
        const amountA = a.amount ? parseFloat(a.amount) : 0;
        const amountB = b.amount ? parseFloat(b.amount) : 0;
        return amountA - amountB;
      }
    },
    {
      title: indexerT('deposits.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string | undefined) => {
        if (!status) {
          return <Text type="secondary">{indexerT('deposits.noStatus', 'Unknown')}</Text>;
        }
        return <FixedStatusTag status={status} />;
      },
      filters: statusFilters,
      filteredValue: statusFilter ? [statusFilter] : null,
      onFilter: (value: any, record: DepositTransaction) => {
        if (!record.status) return false;
        return record.status === value;
      }
    },
    {
      title: indexerT('deposits.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string | undefined) => {
        if (!date) {
          return <Text type="secondary">{indexerT('deposits.noDate', 'No date')}</Text>;
        }
        return (
          <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
            {dayjs(date).fromNow()}
          </Tooltip>
        );
      },
      sorter: (a: DepositTransaction, b: DepositTransaction) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateA - dateB;
      },
      defaultSortOrder: 'descend' as const
    },
    {
      title: indexerT('deposits.isQueued'),
      dataIndex: 'is_queued',
      key: 'is_queued',
      render: (isQueued: boolean | undefined) => {
        if (isQueued === undefined) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tag color={isQueued ? 'green' : 'orange'}>
            {isQueued ? indexerT('deposits.yes') : indexerT('deposits.no')}
          </Tag>
        );
      },
      filters: [
        { text: indexerT('deposits.yes'), value: true },
        { text: indexerT('deposits.no'), value: false }
      ],
      onFilter: (value: any, record: DepositTransaction) => {
        if (record.is_queued === undefined) return false;
        return record.is_queued === value;
      }
    },
    {
      title: indexerT('deposits.actions'),
      key: 'actions',
      render: (_: any, record: DepositTransaction) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/transactions/details/${record._id}`)}
            size="small"
          >
            {indexerT('deposits.view')}
          </Button>
          {record.txid ? (
            <Button
              icon={<SearchOutlined />}
              onClick={() => {
                // Get the blockchain from the record if available
                const blockchain = record.blockchain ||
                  (record.network_id && typeof record.network_id === 'object' ?
                    record.network_id.blockchain : 'ethereum');

                // Determine explorer URL based on blockchain
                let explorerUrl = '';
                switch(blockchain.toLowerCase()) {
                  case 'ethereum':
                    explorerUrl = `https://etherscan.io/tx/${record.txid}`;
                    break;
                  case 'ethereum_holesky':
                    explorerUrl = `https://holesky.etherscan.io/tx/${record.txid}`;
                    break;
                  case 'bsc':
                    explorerUrl = `https://bscscan.com/tx/${record.txid}`;
                    break;
                  case 'bsc_testnet':
                    explorerUrl = `https://testnet.bscscan.com/tx/${record.txid}`;
                    break;
                  case 'solana':
                    explorerUrl = `https://solscan.io/tx/${record.txid}`;
                    break;
                  case 'solana_devnet':
                    explorerUrl = `https://solscan.io/tx/${record.txid}?cluster=devnet`;
                    break;
                  case 'polygon':
                    explorerUrl = `https://polygonscan.com/tx/${record.txid}`;
                    break;
                  case 'arbitrum_one':
                    explorerUrl = `https://arbiscan.io/tx/${record.txid}`;
                    break;
                  case 'avalanche_c_chain_fuji':
                    explorerUrl = `https://testnet.snowtrace.io/tx/${record.txid}`;
                    break;
                  case 'ton':
                    explorerUrl = `https://tonscan.org/tx/${record.txid}`;
                    break;
                  case 'bitcoin':
                    explorerUrl = `https://www.blockchain.com/explorer/transactions/btc/${record.txid}`;
                    break;
                  case 'tron':
                    explorerUrl = `https://tronscan.org/#/transaction/${record.txid}`;
                    break;
                  default:
                    explorerUrl = `https://etherscan.io/tx/${record.txid}`;
                }

                window.open(explorerUrl, '_blank');
              }}
              size="small"
            >
              {indexerT('deposits.explorer')}
            </Button>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <Card
      title={indexerT('deposits.recentTitle')}
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isLoading}
        >
          {commonT('actions.refresh')}
        </Button>
      }
    >
      {isLoading && !deposits ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      ) : isError || !deposits || !(deposits as any)?.data || (deposits as any)?.data?.length === 0 ? (
        <Empty description={indexerT('deposits.noData')} />
      ) : (
        <Table
          columns={columns}
          dataSource={(deposits as any)?.data || []}
          rowKey="_id"
          onChange={handleTableChange}
          pagination={{
            ...pagination,
            total: (deposits as any)?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => indexerT('deposits.showTotal', {
              from: (pagination.current - 1) * pagination.pageSize + 1,
              to: Math.min(pagination.current * pagination.pageSize, total),
              total
            })
          }}
          size="small"
        />
      )}
    </Card>
  );
};

export default RecentDepositsPanel;
